/* Quién eres. La sesión vive en una cookie que el navegador manda solo (HttpOnly: la página no la ve); aquí
   solo se sabe si hay sesión y de quién. Sin conexión no se te echa: se sigue con lo guardado en el móvil. */
import { useSyncExternalStore } from 'react';

export interface Persona { usuario: string; nombre: string; rol: 'propietario' | 'amigo' }
export interface Estado { fase: 'comprobando' | 'dentro' | 'fuera'; yo: Persona | null; grupo: { usuario: string; nombre: string }[] }

const CLAVE_YO = 'tabiji.yo';
const leer = (): Persona | null => { try { return JSON.parse(localStorage.getItem(CLAVE_YO) ?? 'null'); } catch { return null; } };

let estado: Estado = { fase: 'comprobando', yo: leer(), grupo: [] };
const oyentes = new Set<() => void>();
const cambiar = (nuevo: Estado) => { estado = nuevo; oyentes.forEach(f => f()); };

export const useSesion = (): Estado => useSyncExternalStore(f => { oyentes.add(f); return () => oyentes.delete(f); }, () => estado);
export const sesion = () => estado;

/** Quien guarda datos locales se apunta aquí para borrarlos al cambiar de persona o cerrar sesión. */
const antesDeSalir = new Set<() => Promise<void> | void>();
/** Lo que hay que hacer mientras la sesión aún vale (por ejemplo, dejar de compartir la ubicación). */
export const registrarAntesDeSalir = (f: () => Promise<void> | void) => { antesDeSalir.add(f); };
const alLimpiar = new Set<() => void>();
export const registrarLimpieza = (f: () => void) => { alLimpiar.add(f); };

function limpiarLocal() {
  try {
    for (const k of Object.keys(localStorage)) if (k.startsWith('tabiji.apuntes.') || k.startsWith('tabiji.viaje.')) localStorage.removeItem(k);
  } catch { /* sin almacenamiento */ }
  try { void globalThis.caches?.delete('viajes'); } catch { /* sin caché (http) */ }       // la copia del viaje de la otra persona
  alLimpiar.forEach(f => { try { f(); } catch { /* una limpieza que falla no corta las demás */ } });
}

const guardarYo = (yo: Persona) => { try { localStorage.setItem(CLAVE_YO, JSON.stringify(yo)); } catch { /* sin sitio */ } };

/** Otra persona en este móvil: no se queda con lo apuntado por la anterior. */
function ser(yo: Persona, grupo = estado.grupo) {
  const previo = leer();
  if (previo && previo.usuario !== yo.usuario) limpiarLocal();
  guardarYo(yo);
  cambiar({ fase: 'dentro', yo, grupo });
}

/** Una petición dijo «sin sesión»: se pide entrar (salvo sin red, donde no se puede ni se debe). */
export function noAutorizado() {
  if (navigator.onLine && estado.fase !== 'fuera') cambiar({ ...estado, fase: 'fuera' });
}

export async function comprobarSesion() {
  try {
    const r = await fetch('/api/auth/yo', { cache: 'no-store' });
    if (r.status === 401) { cambiar({ ...estado, fase: 'fuera' }); return; }
    if (!r.ok) throw new Error(String(r.status));
    const d = await r.json();
    ser(d.usuario, d.grupo);
  } catch {
    // Sin red: quien ya entró antes sigue dentro con lo guardado; quien nunca entró, a la pantalla de entrada
    cambiar({ ...estado, fase: estado.yo ? 'dentro' : 'fuera' });
  }
}

async function enviar(url: string, cuerpo: unknown): Promise<{ ok: boolean; estado: number; datos: any }> {
  try {
    const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(cuerpo) });
    return { ok: r.ok, estado: r.status, datos: await r.json().catch(() => ({})) };
  } catch {
    return { ok: false, estado: 0, datos: { error: 'No hay conexión. Para entrar hace falta red.' } };
  }
}

/** Devuelve el mensaje de error, o null si ha entrado. */
export async function entrar(usuario: string, clave: string): Promise<string | null> {
  const r = await enviar('/api/auth/entrar', { usuario, clave });
  if (!r.ok) return r.datos.error ?? 'No se pudo entrar';
  await comprobarSesion();
  return null;
}

export async function salir() {
  for (const f of antesDeSalir) { try { await f(); } catch { /* no impide salir */ } }
  await enviar('/api/auth/salir', {});
  limpiarLocal();
  try { localStorage.removeItem(CLAVE_YO); } catch { /* sin almacenamiento */ }
  cambiar({ fase: 'fuera', yo: null, grupo: [] });
}

export async function cambiarClave(actual: string, nueva: string): Promise<string | null> {
  const r = await enviar('/api/auth/clave', { actual, nueva });
  return r.ok ? null : r.datos.error ?? 'No se pudo cambiar';
}

export async function infoInvitacion(token: string): Promise<{ nombre: string } | null> {
  try {
    const r = await fetch(`/api/auth/invitacion/${encodeURIComponent(token)}`, { cache: 'no-store' });
    return r.ok ? await r.json() : null;
  } catch { return null; }
}

export async function canjearInvitacion(token: string, clave: string): Promise<string | null> {
  const r = await enviar(`/api/auth/invitacion/${encodeURIComponent(token)}`, { clave });
  if (!r.ok) return r.datos.error ?? 'No se pudo guardar';
  await comprobarSesion();
  return null;
}
